import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import CaseStudyCard from '../components/CaseStudyCard';
import IndustrySolutionCard from '../components/IndustrySolutionCard';

const Playground = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState('case-studies');

  // Check for filter parameter from navigation state
  useEffect(() => {
    if (location.state?.filter) {
      setActiveCategory(location.state.filter);
    }
  }, [location.state]);

  // NEW — Only 3 categories
  const categories = [
    { id: 'case-studies', name: 'Case Studies' },
    { id: 'industry-solutions', name: 'Industry Solutions' },
    { id: 'interactive-demos', name: 'Interactive Demos' },
  ];

  // Case studies data
  const caseStudies = [
    {
      id: 1,
      icon: <i className="fi fi-ss-marker"></i>,
      title: 'INmaps',
      description: 'An intelligent indoor navigation system that leverages AI to automatically detect spaces from floor plans and provides interactive routing capabilities.',
      features: [
        'AI-powered space detection from floor plans',
        'Interactive canvas with drag-and-drop editing',
        'Polygon-based room boundary detection',
        'Real-time coordinate transformation',
        'WhatsApp Business API integration',
        'Google Maps compatibility'
      ],
      imageUrl: '/images/case-study-1/video-thumbnail.jpg',
      link: '/case-studies/casestudy1',
    },
    {
      id: 2,
      icon: <i className="fi fi-sr-cubes"></i>,
      title: 'Agentic BIM Tool',
      description: 'AI-powered compliance checker for 3D building models with automated validation against multiple regulatory authorities.',
      features: [
        'IFC Upload - Extract building data to semantic format',
        'Multi-Agency Compliance - Auto-validate against 7 Singapore authorities',
        '3D Viewer - Interactive model with violation highlighting',
        'SPARQL Queries - Real-time rule validation & component search',
        'Scheduled Checks - Automated compliance monitoring on model updates'
      ],
      imageUrl: '/images/case-study-2/video-thumbnail.jpg',
      link: '/case-studies/casestudy2',
    },
  ];

  // Industry solutions data
  const industrySolutions = [
    {
      id: 1,
      icon: <i className="fi fi-ss-apartment text-6xl text-purple-500"></i>,
      title: 'Construction Tech',
      description: 'Smart systems for modern infrastructure workflows and project management.',
      features: [
        'Digital Developer Platforms',
        'Contractor Digitalisation',
        'BIM & IDD Enablement',
        'DfMA & Fabrication Integration',
        'Smart Facilities & Asset Management'
      ],
      imageUrl: '/images/placeholder-construction.jpg',
      link: '/industry-solutions/construction-tech',
    },
    {
      id: 2,
      icon: <i className="fi fi-rs-selling text-6xl text-purple-500"></i>,
      title: 'Ecommerce Solutions',
      description: 'End-to-end digital commerce platforms with:',
      features: [
        'Payment Automation & Reconciliation',
        'Commerce Risk & Fraud Intelligence',
        'Demand & Revenue Analytics',
        'Global Commerce Enablement'
      ],
      imageUrl: '/images/placeholder-fintech.jpg',
      link: '/industry-solutions/fintech',
    },
    {
      id: 3,
      icon: <i className="fi fi-sr-select text-6xl text-purple-500"></i>,
      title: 'Creative Industries',
      description: 'AI-enhanced workflows for digital creators and content production.',
      features: [
        'Asset management',
        'Collaboration tools',
        'Workflow automation',
        'Cloud rendering',
        'Games development',
        'Social media app development'
      ],
      imageUrl: '/images/placeholder-creative.jpg',
      link: '/industry-solutions/creative-industries',
    },
  ];

  // Handle tab selection
  const handleCategoryChange = (id) => {
    setActiveCategory(id);

    // Interactive demos → immediate navigation to GameLibrary.jsx
    if (id === 'interactive-demos') {
      navigate('/library');
    }
  };

  // Render case studies grid
  const renderCaseStudies = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {caseStudies.map((study) => (
          <Link key={study.id} to={study.link}>
            <CaseStudyCard
              icon={study.icon}
              title={study.title}
              description={study.description}
              features={study.features}
              imageUrl={study.imageUrl}
            />
          </Link>
        ))}
      </div>
    </div>
  );

  // Render industry solutions grid
  const renderIndustrySolutions = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {industrySolutions.map((solution) => (
          <Link key={solution.id} to={solution.link}>
            <IndustrySolutionCard
              icon={solution.icon}
              title={solution.title}
              description={solution.description}
              features={solution.features}
              imageUrl={solution.imageUrl}
            />
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-gray-800">

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6">
            STRAY Playground
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Explore interactive samples, solutions and real-world projects.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 border-y border-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap gap-4 justify-center">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                  : 'bg-purple-50 hover:bg-purple-100 text-gray-700 border-2 border-purple-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </section>

      {/* Render grids for Case Studies / Industry Solutions */}
      {activeCategory === 'case-studies' && renderCaseStudies()}
      {activeCategory === 'industry-solutions' && renderIndustrySolutions()}
    </div>
  );
};

export default Playground;
